// @system — Individual blog post page with markdown-style rendering
// @custom — loads from /api/blog/:slug (DB-backed); no static fallback, a missing post is a 404
import { useMemo, useEffect, useState } from 'react'
import { readInitialPage, releaseInitialPage, currentPageKey } from '@/app/lib/@system/initialState'
import { Link, useParams } from 'react-router-dom'
import {
  Clock,
  Calendar,
  ArrowLeft,
  ChevronRight,
  Tag } from 'lucide-react'
import { Header } from '../../../../components/@system/Header'
import { Footer } from '../../../../components/@system/Footer'
import { Card, CardContent } from '../../../../components/@system/Card'
import { Button } from '../../../../components/@system/ui/button'
import { OgMeta } from '../../../../components/@system/OgMeta'
// One date formatter for the listing and the article (locale + time zone pinned — see helpers.js).
import { apiPostToBlogPost, formatDate } from '../BlogPage/helpers'
// Article rendering lives in sibling components: hero (cover), body (content_html → HTML, markdown
// fallback), share row, "On this page" and related posts (API, static fallback for seed posts).
import { ArticleHero } from './ArticleHero'
import { ArticleBody } from './ArticleBody'
import { ShareRow } from './ShareRow'
import { TableOfContents } from './TableOfContents'
import { RelatedPosts } from './RelatedPosts'
import { extractHeadings } from './article-helpers'

/** @typedef {import('../BlogPage/helpers').BlogPost} BlogPost */
/** @typedef {import('../BlogPage/helpers').ApiBlogPost} ApiBlogPost */

// ── Page ───────────────────────────────────────────────────────────────────────

export function BlogPostPage() {
  const { slug } = useParams()

  // Server-rendered documents inline GET /api/blog/:slug (lib/@system/PublicState → BlogRepo, one
  // query source): `{ post }` or `{ post: null }` (the server also answered HTTP 404). The first
  // render — server and hydrating client — is the final article or the not-found page. The
  // payload is for THIS slug only (`seededSlug`): navigating to another article (same component,
  // new `slug`) fetches as before; the mount effect releases the payload.
  const [seeded] = useState(() => /** @type {{ post: ApiBlogPost|null }|null} */ (readInitialPage('blogPost', currentPageKey())))
  const [seededSlug] = useState(() => (seeded ? slug : null))
  // null = not loaded or API unavailable (nothing is shown), 'not-found' = server said 404
  const [apiPost, setApiPost] = useState(/** @returns {BlogPost | 'not-found' | null} */ () => {
    if (!seeded) return null
    return seeded.post ? apiPostToBlogPost(seeded.post) : 'not-found'
  })
  const [loading, setLoading] = useState(() => !seeded)

  useEffect(() => {
    if (seeded && slug === seededSlug) { releaseInitialPage(); return undefined }
    if (!slug) { setLoading(false); return undefined }
    let cancelled = false
    fetch(`/api/blog/${slug}`)
      .then((r) => {
        if (r.status === 404) { if (!cancelled) setApiPost('not-found'); return null }
        if (!r.ok) throw new Error()
        return r.json()
      })
      .then((/** @type {{ post: ApiBlogPost } | null} */ data) => {
        if (data && !cancelled) setApiPost(apiPostToBlogPost(data.post))
      })
      .catch(() => {
        // API unavailable: nothing to show (never the template's sample posts)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [slug, seeded, seededSlug])

  // Only a post the API answered is shown; the template's sample articles are never a real page
  // (dev-rankradar, 2026-10-12: six sample posts listed, every "Read article" a 404).
  const post = apiPost === 'not-found' || apiPost === null ? undefined : apiPost

  const headings = useMemo(() => extractHeadings(post?.contentHtml), [post])
  // Server rendering has no `window`; the share URL is only needed once hydrated.
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Header />
        <main id="main-content" tabIndex={-1} className="flex items-center justify-center py-32 text-brand-text-muted text-sm focus:outline-none" aria-busy="true">
          Loading…
        </main>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Header />
        <main id="main-content" tabIndex={-1} className="flex flex-col items-center justify-center py-32 text-center px-4 focus:outline-none">
          <p className="text-5xl font-bold mb-4" aria-hidden="true">404</p>
          <h1 className="text-brand-text-muted mb-8 text-base font-normal">Post not found.</h1>
          <Link to="/blog">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Blog
            </Button>
          </Link>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* `url` only when the post declares a canonical: otherwise OgMeta resolves
          window.location.origin + pathname inside its effect (exactly /blog/<slug>) —
          reading `window` here would throw during server rendering. */}
      <OgMeta
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        type="article"
        url={post.canonicalUrl || undefined}
        image={post.ogImage || post.coverImage || undefined}
      />
      <Header />

      <main id="main-content" className="container mx-auto px-4 py-10 max-w-5xl">
        {/* ── Breadcrumb ─────────────────────────────────────────────────── */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-brand-text-muted mb-8">
          <Link to="/blog" className="hover:text-brand-text transition-colors">
            Blog
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="rounded-full bg-brand-primary/10 text-brand-primary px-2 py-0.5 font-medium">
            {post.category}
          </span>
          <ChevronRight className="h-3 w-3" />
          <span className="text-brand-text truncate max-w-[200px]">{post.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* ── Post body ─────────────────────────────────────────────────── */}
          <article className="flex-1 min-w-0">
            <header className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold leading-snug">{post.title}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-brand-text-muted">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(post.publishedAt)}
                </span>
                {typeof post.readingTime === 'number' && post.readingTime > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {post.readingTime} min read
                  </span>
                )}
                <span>{post.author}</span>
                {(post.tags ?? []).map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 rounded-full bg-brand-surface px-2 py-0.5 text-xs font-medium"
                  >
                    <Tag className="h-2.5 w-2.5" />
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <ArticleHero post={post} />

            <Card>
              <CardContent className="pt-6 pb-8">
                <ArticleBody post={post} />
              </CardContent>
            </Card>

            <ShareRow url={`${origin}/blog/${post.slug}`} title={post.title} />

            <div className="mt-6">
              <Link
                to="/blog"
                className="min-h-6 flex items-center gap-2 text-sm text-brand-text-muted hover:text-brand-text transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Blog
              </Link>
            </div>
          </article>

          {/* ── Sidebar ─────────────────────────────────────────────────────── */}
          <aside className="lg:w-64 flex-shrink-0 space-y-8">
            <TableOfContents headings={headings} />
            <RelatedPosts current={post} />
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  )
}
